import { makeObservable, observable, computed, action } from "mobx";
import { nanoid } from "nanoid";
import {
  jsonCopy,
  isValidDate,
  getLastPublicAppsList,
  getSizeInBytes,
} from "../utils/helper";

export class StoreSharing {
  isLoaded = false;
  teamItemsLoaded = false;
  notificationsList = observable([]);
  notificationMarker = false;
  notificationsLoaded = false;
  teamItemsList = observable([]);
  publicItemsList = observable([]);
  invitationsInbound = [];
  invitationsOutbound = [];
  invitationStatus = [
    {
      id: "invited",
      name: "Invited",
      nameShort: "Invited",
      progress: 0,
      color: "lightgrey",
      selectable: false,
    },
    {
      id: "opened",
      name: "Opened",
      nameShort: "Opened",
      progress: 10,
      color: "blue",
      selectable: true,
    },
    {
      id: "progress25",
      name: "In progress - 25%",
      nameShort: "25%",
      progress: 25,
      color: "purple",
      selectable: true,
    },
    {
      id: "progress50",
      name: "In progress - 50%",
      nameShort: "50%",
      progress: 50,
      color: "purple",
      selectable: true,
    },
    {
      id: "progress75",
      name: "In progress - 75%",
      nameShort: "75%",
      progress: 75,
      color: "purple",
      selectable: true,
    },
    {
      id: "helpneeded",
      name: "Help needed",
      nameShort: "help",
      progress: 0,
      color: "magenta",
      selectable: true,
    },
    {
      id: "review",
      name: "Review",
      nameShort: "review",
      progress: 99,
      color: "blue",
      selectable: false,
    },
    {
      id: "done",
      name: "Done",
      nameShort: "done",
      progress: 100,
      color: "green",
      selectable: true,
    },
    {
      id: "overdue",
      name: "Overdue",
      nameShort: "Overdue",
      progress: 0,
      color: "volcano",
      selectable: false,
    },
    {
      id: "cancelled",
      name: "Cancelled",
      nameShort: "Cancelled",
      progress: 100,
      color: "purple",
      selectable: false,
    },
  ];

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.notificationsList = [];
    this.teamItemsList = [];
    this.lastUpdate = new Date();

    makeObservable(this, {
      isLoaded: observable,
      setIsLoaded: action,
      notificationsList: observable,
      notificationsLoaded: observable,
      notificationMarker: observable,
      setNotificationMarker: action,
      setNotificationsLoaded: action,
      invitationsInbound: observable,
      invitationsOutbound: observable,
      setInvitationsInbound: action,
      getInvitationsInboundCount: computed,
      addInvitationsInbound: action,
      removeInvitationsInbound: action,
      setInvitationsOutbound: action,
      addInvitationsOutbound: action,
      removeInvitationsOutbound: action,
      updateInvitationsInboundField: action,
      updateInvitationsOutboundField: action,
      addTeamItem: action,
      updateTeamItemField: action,
      updatedInvitationStatusFromNotification: action,
      getNotificationsCount: computed,
      setNewNotification: action,
      setNotifications: action,
      teamItemsList: observable,
      removeTeamItem: action,
      publicItemsList: observable,
      setPublicItemsList: action,
      addPublicItem: action,
    });
  }

  getStatusDescription(status) {
    let description = this.invitationStatus.find(
      (statusInfo) => statusInfo.id === status,
    )?.name;
    return description;
  }

  getStatusInfo(status) {
    let info = this.invitationStatus.find(
      (statusInfo) => statusInfo.id === status,
    );
    return info;
  }

  getInvitationUrl(
    shareId,
    linkType = "default",
    elementType = "element",
    getHostUrl = false,
  ) {
    let prefix = linkType === "public" ? "xr3b_" : "_";
    // billed invitations are also public
    if (linkType === "billed") prefix = "xr4b_";
    let host = getHostUrl ? this.rootStore.main.getHostUrl() : "";

    return host + "/" + elementType + "/" + prefix + shareId;
  }

  getInvitationInboundIndexById(id) {
    const foundIndex = this.invitationsInbound.findIndex(
      (invitation) => invitation.id === id,
    );
    return foundIndex;
  }

  getInvitationOutboundIndexById(id) {
    const foundIndex = this.invitationsOutbound.findIndex(
      (invitation) => invitation.id === id,
    );
    return foundIndex;
  }

  getInvitationRecipient(item) {
    if (!item) return "undefined";

    let recipient = item.sharedWith;
    if (!recipient || recipient === "undefined") {
      if (item.data?.linkType === "organization") recipient = "Team Items";
      if (item.data?.linkType === "public") recipient = "Public";
    }
    return recipient;
  }

  async getPageDetail(id) {
    if (!id) return null;

    let items = await this.rootStore.appsync.getCreatorInvitations(
      id,
      "personalpage",
    );

    // get external items
    if (id === "stephan@airrange.io") {
      let list = [
        "LimYDLLtPsQDEydc4SjEm",
        "2K3UM_2OAdEs9VnpouToy",
        "Wt5RyiwnM6Kx1UVWJm2WY",
      ];

      for (let i = 0; i < list.length; i++) {
        let externalItem = await this.rootStore.appsync.getInvitationForPage(
          list[i],
        );
        items.push(externalItem);
      }
    }
    let detail = {
      pageId: id,
      title: id,
      // emailPublisher: "stephan@airrange.io",
      description: "",
      cardImageUrl:
        "https://airrange-content.s3.eu-central-1.amazonaws.com/graphics/patterns/webp/pattern_23.webp",
      topImageUrl:
        "https://airrange-content.s3.eu-central-1.amazonaws.com/graphics/patterns/webp/pattern_23.webp",
      authorImageUrl:
        id === "stephan@airrange.io"
          ? "https://airrange-customers.s3.eu-central-1.amazonaws.com/authors/airrange/stephan-methner.jpg"
          : null,
      items: items,
      documentHeader:
        "<div>Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.</div>",
    };
    return detail;
  }

  getTeamItemIndexById(id) {
    const foundIndex = this.teamItemsList.findIndex(
      (invitation) => invitation.id === id,
    );
    return foundIndex;
  }

  // =================================
  // Invitations
  // =================================

  async updateInvitationDetail(sharingInfo, shareValues) {
    if (!sharingInfo) return;
    const shareId = sharingInfo.id ?? sharingInfo.shareId;
    let linkType = sharingInfo.data.linkType;
    let shareData = jsonCopy(sharingInfo.data);
    let title = shareValues?.title;
    // set the new values
    shareData.instructions = shareValues?.instructions;
    shareData.password = shareValues?.password;
    shareData.personalPage = shareValues?.personalPage ?? false;
    shareData.removeSharing = shareValues?.removeSharing;
    shareData.removeScenarios = shareValues?.removeScenarios;
    shareData.startFullscreen = shareValues?.startFullscreen;
    shareData.activeMonetize = shareValues?.activeMonetize;
    shareData.priceId = shareValues?.activeMonetize
      ? shareValues?.price?.id
      : "";
    shareData.priceWeek = shareValues?.activeMonetize
      ? shareValues?.price?.week
      : false;
    shareData.priceMonth = shareValues?.activeMonetize
      ? shareValues?.price?.month
      : false;
    shareData.trial = shareValues?.activeMonetize
      ? shareValues?.trial
      : undefined;

    if (isValidDate(shareValues?.validuntil?._d)) {
      shareData.validUntil = shareValues.validuntil?._d;
    }
    if (isValidDate(shareValues?.validuntil)) {
      shareData.validUntil = shareValues.validuntil;
    }

    const result = await this.rootStore.appsync.updateUserInvitation(
      shareId,
      shareValues,
      shareData,
      linkType,
    );

    if (result) {
      this.updateInvitationsInboundField(shareId, "name", title);
      this.updateInvitationsOutboundField(shareId, "name", title);
      this.updateTeamItemField(shareId, "name", title);
    }
    return result;
  }

  async updateInvitationDesignInfo(shareId, designInfo) {
    const result = await this.rootStore.appsync.updateUserInvitationDesignInfo(
      shareId,
      designInfo,
    );
    return result;
  }

  async updateInvitationStatus(sharingInfo, status, comment) {
    if (!sharingInfo) return;
    const shareId = sharingInfo.id ?? sharingInfo.shareId;

    const result = await this.rootStore.appsync.updateUserInvitationStatus(
      sharingInfo,
      status,
      comment,
    );
    if (result) {
      // update the inbound list
      let foundIndex = this.getInvitationInboundIndexById(shareId);
      if (foundIndex !== -1) {
        const tempItem = this.invitationsInbound[foundIndex];
        tempItem.status = status;
        this.invitationsInbound.splice(foundIndex, 1, tempItem);
      }
      // update the outbound list
      foundIndex = this.getInvitationOutboundIndexById(shareId);
      if (foundIndex !== -1) {
        const tempItem = this.invitationsOutbound[foundIndex];
        tempItem.status = status;
        this.invitationsOutbound.splice(foundIndex, 1, tempItem);
      }
    }
    return result;
  }

  async updateInvitationStatusIsDeleted(item) {
    if (!item) return;
    const shareId = item.id ?? item.shareId;

    const result =
      await this.rootStore.appsync.updateUserInvitationIsDeleted(shareId);
    if (result) {
      // update the inbound list
      this.removeInvitationsInbound(shareId);
      // update the outbound list
      this.removeInvitationsOutbound(shareId);
      // update the teamitems list
      this.removeTeamItem(shareId);
    }
    return result;
  }

  async updateInvitationStatusIsArchived(item) {
    if (!item) return;
    const shareId = item.id ?? item.shareId;

    const result =
      await this.rootStore.appsync.updateUserInvitationIsArchived(shareId);
    if (result) {
      // update the inbound list
      this.removeInvitationsInbound(shareId);
      // update the outbound list
      this.removeInvitationsOutbound(shareId);
      // update the teamitems list
      this.removeTeamItem(shareId);
    }
    return result;
  }

  async updatedInvitationStatusFromNotification(statusUpdate) {
    if (!statusUpdate) return;
    // update the inbound list
    let foundIndex = this.getInvitationInboundIndexById(statusUpdate.id);
    if (foundIndex !== -1) {
      const tempItem = this.invitationsInbound[foundIndex];
      tempItem.status = statusUpdate.status;
      tempItem.isDeleted = statusUpdate.isDeleted;
      tempItem.isArchived = statusUpdate.isArchived;
      tempItem.modified = statusUpdate.modified;
      this.invitationsInbound.splice(foundIndex, 1, tempItem);
    }
    // update the outbound list
    foundIndex = this.getInvitationOutboundIndexById(statusUpdate.id);
    if (foundIndex !== -1) {
      const tempItem = this.invitationsOutbound[foundIndex];
      tempItem.status = statusUpdate.status;
      this.invitationsOutbound.splice(foundIndex, 1, tempItem);
    }
  }

  setInvitationsInbound(invitationsInbound) {
    this.invitationsInbound = invitationsInbound ?? [];
    // this.getInvitationLiveDetails();
    this.setIsLoaded(true);
  }

  addInvitationsInbound(newInvitation) {
    if (!newInvitation) return;
    this.invitationsInbound.push(newInvitation);
  }

  removeInvitationsInbound(shareId) {
    // update the inbound list
    const foundIndex = this.getInvitationInboundIndexById(shareId);
    if (foundIndex !== -1) {
      this.invitationsInbound.splice(foundIndex, 1);
    }
  }

  updateInvitationsInboundField(shareId, fieldName, fieldValue) {
    // update the inbound list
    const foundIndex = this.getInvitationInboundIndexById(shareId);
    if (foundIndex !== -1) {
      const tempItem = this.invitationsInbound[foundIndex];
      tempItem[fieldName] = fieldValue;
      this.invitationsInbound.splice(foundIndex, 1);
      this.addInvitationsInbound(tempItem);
    }
  }

  setInvitationsOutbound(invitationsOutbound) {
    this.invitationsOutbound = invitationsOutbound ?? [];
    this.setIsLoaded(true);
  }

  setPublicItemsList(list) {
    this.publicItemsList = list ?? [];
  }

  addInvitationsOutbound(newInvitation) {
    if (!newInvitation) return;
    this.invitationsOutbound.push(newInvitation);
  }

  updateInvitationsOutboundField(shareId, fieldName, fieldValue) {
    // update the inbound list
    const foundIndex = this.getInvitationOutboundIndexById(shareId);
    if (foundIndex !== -1) {
      const tempItem = this.invitationsOutbound[foundIndex];
      tempItem[fieldName] = fieldValue;
      this.invitationsOutbound.splice(foundIndex, 1);
      this.addInvitationsOutbound(tempItem);
    }
  }

  removeInvitationsOutbound(shareId) {
    // update the outbound list
    const foundIndex = this.getInvitationOutboundIndexById(shareId);
    if (foundIndex !== -1) {
      this.invitationsOutbound.splice(foundIndex, 1);
    }
  }

  get getInvitationsInboundCount() {
    return this.invitationsInbound?.length ?? 0;
  }

  async getTeamItems() {
    if (!this.rootStore.main.userTenantId) return;

    const items = await this.rootStore.appsync.getFolderInvitations(
      this.rootStore.main.userTenantId,
    );
    this.teamItemsList = items;
    this.teamItemsLoaded = true;
  }

  async checkTeamItems() {
    if (!this.teamItemsLoaded) await this.getTeamItems();
  }

  updateTeamItemField(shareId, fieldName, fieldValue) {
    // update the inbound list
    const foundIndex = this.getTeamItemIndexById(shareId);
    if (foundIndex !== -1) {
      const tempItem = this.teamItemsList[foundIndex];
      tempItem[fieldName] = fieldValue;
      this.teamItemsList.splice(foundIndex, 1);
      this.addTeamItem(tempItem);
    }
  }

  addTeamItem(newInvitation) {
    if (!newInvitation) return;
    this.teamItemsList.push(newInvitation);
  }

  removeTeamItem(shareId) {
    // update the inbound list
    const foundIndex = this.getTeamItemIndexById(shareId);
    if (foundIndex !== -1) {
      this.teamItemsList.splice(foundIndex, 1);
    }
  }

  //=================================================================
  //
  // Scenarios
  //
  //=================================================================

  async optimizeScenarioData(dataJson) {
    const dataSize = getSizeInBytes(dataJson);
    if (!dataJson || !dataJson.elementValues) return;

    for (const element of dataJson.elementValues) {
      if (element.direction === "out") {
        element["label"] && delete element.label;
        element["oldValue"] && delete element.oldValue;
        element["element"] && delete element.element;
        element["field_name"] && delete element.field_name;
        element["direction"] && delete element.direction;
      }
      if (element.direction === "in") {
        element["label"] && delete element.label;
        element["oldValue"] && delete element.oldValue;
      }
    }
    const dataSizeAfter = getSizeInBytes(dataJson);
    if (dataSizeAfter > 300000) {
      // we need to delete more
    }
    console.log("INFO optimized data size: ", dataSize, dataSizeAfter);
  }

  async createInvitationScenario(
    invitationId,
    values,
    dataJson,
    storageType = "local",
  ) {
    let title = values?.title;
    let elementValues = dataJson?.elementValues;
    await this.optimizeScenarioData(dataJson);

    if (!elementValues || !title) return;

    if (storageType === "local") {
      // add a record
      let scenarios = this.rootStore.main.getLocalStoreValue(
        invitationId + "-scenarios",
        [],
      );
      scenarios.push({
        id: nanoid(),
        title: title,
        type: "scenario",
        created: new Date(),
        invitationId: invitationId,
        elementValues: elementValues,
      });
      this.rootStore.main.setLocalStoreValue(
        invitationId + "-scenarios",
        scenarios,
      );
    }

    if (storageType === "cloud") {
      await this.rootStore.appsync.createInvitationScenario(
        invitationId,
        values,
        dataJson,
      );
    }
  }

  async updateInvitationScenario(
    invitationId,
    scenarioId,
    dataJson,
    storageType = "local",
  ) {
    let elementValues = dataJson?.elementValues;
    if (!elementValues) return;

    if (storageType === "local") {
      let scenarios = this.rootStore.main.getLocalStoreValue(
        invitationId + "-scenarios",
        [],
      );
      // look for scenario ID's
      const foundIndex = scenarios.findIndex((x) => x.id === scenarioId);
      if (foundIndex > -1) {
        scenarios[foundIndex].elementValues = elementValues;
        this.rootStore.main.setLocalStoreValue(
          invitationId + "-scenarios",
          scenarios,
        );
      }
    }

    if (storageType === "cloud") {
      await this.rootStore.appsync.updateInvitationScenario(
        invitationId,
        scenarioId,
        dataJson,
      );
    }
  }

  async getInvitationScenario(invitationId, scenarioId, storageType = "local") {
    if (storageType === "local") {
      let scenarios = await this.rootStore.main.getLocalStoreValue(
        invitationId + "-scenarios",
        [],
      );
      let foundScenario = scenarios.find((obj) => {
        return obj.id === scenarioId;
      });
      return foundScenario;
    }
    if (storageType === "cloud") {
      const res = await this.rootStore.appsync.getInvitationScenario(
        invitationId,
        scenarioId,
      );
      return res;
    }
  }

  async deleteInvitationScenario(
    invitationId,
    scenarioId,
    storageType = "local",
  ) {
    if (storageType === "local") {
      let scenarios = this.rootStore.main.getLocalStoreValue(
        invitationId + "-scenarios",
        [],
      );
      const newScenarios = scenarios.filter((x) => x.id !== scenarioId);

      this.rootStore.main.setLocalStoreValue(
        invitationId + "-scenarios",
        newScenarios,
      );
    }

    if (storageType === "cloud") {
      await this.rootStore.appsync.deleteInvitationScenario(
        invitationId,
        scenarioId,
      );
    }
  }

  async getInvitationScenarios(invitationId, storageType = "local") {
    // get scenarios from local storage
    let scenarios = [];

    if (storageType === "local") {
      scenarios = await this.rootStore.main.getLocalStoreValue(
        invitationId + "-scenarios",
        [],
      );
    }

    // get scenarios from server
    if (storageType === "cloud") {
      const res =
        await this.rootStore.appsync.getInvitationScenariosByUser(invitationId);
      scenarios = res ? res : [];
    }

    scenarios?.forEach((scenario) => {
      scenario.storageType = storageType;
    });
    return scenarios;
  }

  //=================================================================
  //
  // #region PUBLIC ITEMS

  async getPublicItemsFromStorage() {
    // load the public items from the local storage
    this.setPublicItemsList(getLastPublicAppsList());
  }

  async addPublicItem(doc) {
    await this.getPublicItemsFromStorage();
    const tempDocument = {
      id: doc.id,
      type: doc.type,
      elementType: doc.elementType,
      name: doc.name,
      caption: doc.name,
      parametersUrl: doc.parametersUrl,
      designInfo: doc.designInfo,
      timeStamp: doc.timeStamp,
    };
    // get existing document
    const currentElem = this.publicItemsList.find((i) => i.id === doc.id);
    if (
      !tempDocument.designInfo?.backgroundImageUrl &&
      currentElem?.designInfo?.backgroundImageUrl
    ) {
      tempDocument.designInfo ||= {};
      tempDocument.designInfo.backgroundImageUrl =
        currentElem?.designInfo?.backgroundImageUrl;
    }

    // remove old same documents
    const tempList = this.publicItemsList.filter((x) => x.id !== doc.id);
    tempList.unshift(tempDocument);
    const itemsList = [...tempList.slice(0, 20)];
    this.setPublicItemsList(itemsList);
    this.rootStore.main.setLocalStoreValue("publicList-all", itemsList);
  }

  // #endregion
  //
  //=================================================================

  async sendProtocolDataToEmail(
    email = "stephan@airrange.io",
    emailCC,
    emailBCC,
    emailBody = "",
    emailSubject,
    invitationId,
    data,
  ) {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");

    const body = {
      invitationId: invitationId,
      email: email,
      emailCC: emailCC,
      emailBCC: emailBCC,
      emailBody: emailBody,
      emailSubject: emailSubject ?? "New data from airrange",
      data: data ?? [],
    };

    const options = {
      method: "POST",
      headers,
      mode: "cors",
      body: JSON.stringify(body),
      redirect: "follow",
      referrerPolicy: "no-referrer",
    };

    fetch("https://eost9dkz4iski1z.m.pipedream.net", options)
      .then((json) => {
        //console.log("success", json); // Handle success
      })
      .catch((err) => {
        console.log("sendProtocolDataToEmail error", err, options); // Handle errors
      });
  }

  //=================================================================
  //
  // NOTIFICATIONS
  //
  //=================================================================

  setNotificationMarker(value) {
    this.notificationMarker = value;
  }

  setNewNotification(notification) {
    this.notificationsList.push({
      ...notification,
      timeStamp: new Date(),
      isNew: true,
    });
    this.setNotificationMarker(true);
    // timeStamp: new Date(),
    // id: notification.id,
    // type: notification.type,
    // name: notification.name,
    // status: notification.status,
  }

  setNotifications(notifications) {
    if (notifications) {
      this.notificationsList = notifications;
    }
    this.setNotificationsLoaded(true);
  }

  setNotificationsLoaded(loaded) {
    this.notificationsLoaded = loaded;
  }

  get getNotificationsCount() {
    return this.notificationsList.length;
  }

  async getUserNotifications() {
    this.rootStore.appsync.getUserNotifications();
    return;
  }

  setIsLoaded(loaded) {
    this.isLoaded = loaded;
  }

  async sendElementMail(sender, invitationDetail) {
    const elementUrl =
      "https://app.airrange.io/#/element/" + invitationDetail.elementUrl;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    let recipients = invitationDetail.emailRecipients ?? []; //?.join(";") ?? "";

    const body = {
      email: invitationDetail.recipient,
      subject: invitationDetail.emailTitle || "Excel element",
      elementUrl: elementUrl,
      sender: sender,
      recipients: recipients,
      instructions: invitationDetail.emailInstructions || "",
    };

    const options = {
      method: "POST",
      headers,
      mode: "cors",
      body: JSON.stringify(body),
    };

    await fetch("https://eouygnrca62kowh.m.pipedream.net", options);
  }

}
